import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, UtensilsCrossed, Calendar, Clock, Users, 
    Wine, ChefHat, Star, ChevronRight, Phone, Plus, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DiningPage() {
    const navigate = useNavigate();
    const { organization } = useOrganization();
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('menus');
    const [loading, setLoading] = useState(true);
    
    // Data states
    const [dailyMenu, setDailyMenu] = useState(null);
    const [myReservations, setMyReservations] = useState([]);
    const [diningEvents, setDiningEvents] = useState([]);
    
    // Reservation modal
    const [reservationModal, setReservationModal] = useState(false);
    const [reservationForm, setReservationForm] = useState({
        date: '',
        time: '12:30',
        party_size: 2,
        special_requests: ''
    });

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch daily menu
            const today = new Date().toISOString().split('T')[0];
            const menuRes = await axios.get(`${API}/dining/daily-menu?date=${today}`, { headers });
            setDailyMenu(menuRes.data);
            
            // Fetch my reservations
            const resRes = await axios.get(`${API}/dining/reservations`, { headers });
            setMyReservations(resRes.data);
            
            // Fetch dining events
            const eventsRes = await axios.get(`${API}/dining/events`);
            setDiningEvents(eventsRes.data);
        } catch (error) {
            console.error('Error fetching dining data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateReservation = async () => {
        if (!reservationForm.date || !reservationForm.time) {
            toast.error('Please select a date and time');
            return;
        }
        
        try {
            await axios.post(`${API}/dining/reservations`, reservationForm, { headers });
            toast.success('Reservation request submitted!');
            setReservationModal(false);
            setReservationForm({ date: '', time: '12:30', party_size: 2, special_requests: '' });
            fetchData();
        } catch (error) {
            toast.error('Failed to create reservation');
        }
    };

    const handleCancelReservation = async (id) => {
        try {
            await axios.delete(`${API}/dining/reservations/${id}`, { headers });
            toast.success('Reservation cancelled');
            fetchData();
        } catch (error) {
            toast.error('Failed to cancel reservation');
        }
    };

    // Fallback menu if no data
    const fallbackMenu = {
        date: new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
        sections: [
            {
                title: 'Starters',
                items: [
                    { name: 'Soup of the Day', description: 'Roasted butternut squash with toasted seeds', price: 'Included' },
                    { name: 'Smoked Salmon', description: 'With capers, lemon, and wholemeal toast', price: '£8.50' },
                    { name: 'Caesar Salad', description: 'Crisp romaine, parmesan, croutons, classic dressing', price: '£7.00' },
                ]
            },
            {
                title: 'Main Courses',
                items: [
                    { name: 'Roast Chicken', description: 'Free-range, seasonal vegetables, roast potatoes', price: '£16.50' },
                    { name: 'Pan-fried Sea Bass', description: 'New potatoes, samphire, lemon butter', price: '£18.00' },
                    { name: 'Wild Mushroom Risotto', description: 'Arborio rice, truffle oil, parmesan', price: '£14.50', is_vegetarian: true },
                ]
            },
            {
                title: 'Desserts',
                items: [
                    { name: 'Sticky Toffee Pudding', description: 'Butterscotch sauce, vanilla ice cream', price: '£6.50' },
                    { name: 'Fresh Fruit Salad', description: 'Seasonal fruits with mint', price: '£5.00' },
                    { name: 'Cheese Selection', description: 'British cheeses with biscuits and chutney', price: '£8.00' },
                ]
            }
        ]
    };

    // Group menu items by category
    const groupedMenu = dailyMenu?.items?.length > 0 ? 
        dailyMenu.items.reduce((acc, item) => {
            const cat = item.category || 'main';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(item);
            return acc;
        }, {}) : null;

    const getCategoryTitle = (cat) => {
        const titles = { starter: 'Starters', main: 'Main Courses', dessert: 'Desserts', side: 'Sides', beverage: 'Beverages', special: 'Chef\'s Specials' };
        return titles[cat] || cat;
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-amber-100 text-amber-700',
            confirmed: 'bg-green-100 text-green-700',
            cancelled: 'bg-gray-100 text-gray-500',
        };
        return colors[status] || 'bg-gray-100 text-gray-600';
    };

    return (
        <div className="min-h-screen bg-stone-50 pb-24" data-testid="dining-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-rose-800 to-rose-900 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-rose-200 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Home
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <UtensilsCrossed className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">Dining & Hospitality</h1>
                        <p className="text-white/80 text-sm">Menus, reservations & events</p>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="px-4 -mt-4 relative z-10">
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-1 flex">
                    {['menus', 'reservations', 'events'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                                activeTab === tab
                                    ? 'bg-rose-600 text-white'
                                    : 'text-stone-600 hover:bg-stone-100'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="px-4 mt-6">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-rose-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Menus Tab */}
                        {activeTab === 'menus' && (
                            <div className="space-y-6">
                                {/* Today's Menu Card */}
                                <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                                    <div className="bg-rose-50 px-5 py-4 border-b border-rose-100">
                                        <div className="flex items-center gap-2 text-rose-800">
                                            <ChefHat className="w-5 h-5" />
                                            <span className="font-semibold">Today's Menu</span>
                                        </div>
                                        <p className="text-rose-600 text-sm mt-1">
                                            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                                        </p>
                                        {dailyMenu?.chef_special && (
                                            <p className="text-rose-700 text-sm mt-2 italic">
                                                Chef's Special: {dailyMenu.chef_special}
                                            </p>
                                        )}
                                    </div>
                                    
                                    {groupedMenu ? (
                                        Object.entries(groupedMenu).map(([category, items]) => (
                                            <div key={category} className="p-5 border-b border-stone-100 last:border-b-0">
                                                <h3 className="font-semibold text-stone-800 mb-3">{getCategoryTitle(category)}</h3>
                                                <div className="space-y-4">
                                                    {items.map((item, idx) => (
                                                        <div key={idx} className="flex justify-between">
                                                            <div>
                                                                <p className="font-medium text-stone-800">
                                                                    {item.name}
                                                                    {item.is_vegetarian && <span className="ml-2 text-xs text-green-600">(V)</span>}
                                                                    {item.is_vegan && <span className="ml-2 text-xs text-green-600">(VG)</span>}
                                                                </p>
                                                                <p className="text-sm text-stone-500">{item.description}</p>
                                                            </div>
                                                            <span className="text-rose-600 font-medium text-sm whitespace-nowrap ml-4">
                                                                {item.price}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        // Fallback menu
                                        fallbackMenu.sections.map((section, idx) => (
                                            <div key={idx} className="p-5 border-b border-stone-100 last:border-b-0">
                                                <h3 className="font-semibold text-stone-800 mb-3">{section.title}</h3>
                                                <div className="space-y-4">
                                                    {section.items.map((item, itemIdx) => (
                                                        <div key={itemIdx} className="flex justify-between">
                                                            <div>
                                                                <p className="font-medium text-stone-800">
                                                                    {item.name}
                                                                    {item.is_vegetarian && <span className="ml-2 text-xs text-green-600">(V)</span>}
                                                                </p>
                                                                <p className="text-sm text-stone-500">{item.description}</p>
                                                            </div>
                                                            <span className="text-rose-600 font-medium text-sm whitespace-nowrap ml-4">
                                                                {item.price}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Opening Hours */}
                                <div className="bg-white rounded-2xl p-5 border border-stone-200">
                                    <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-rose-600" />
                                        Restaurant Hours
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-stone-600">Breakfast</span>
                                            <span className="text-stone-800">7:30am - 10:00am</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-stone-600">Lunch</span>
                                            <span className="text-stone-800">12:00pm - 2:30pm</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-stone-600">Dinner</span>
                                            <span className="text-stone-800">6:00pm - 9:00pm</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Reservations Tab */}
                        {activeTab === 'reservations' && (
                            <div className="space-y-6">
                                {/* Book a Table */}
                                <div className="bg-white rounded-2xl p-6 border border-stone-200">
                                    <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-rose-600" />
                                        Book a Table
                                    </h3>
                                    <Button 
                                        className="w-full bg-rose-600 hover:bg-rose-700"
                                        onClick={() => setReservationModal(true)}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Make a Reservation
                                    </Button>
                                </div>

                                {/* My Reservations */}
                                <div>
                                    <h3 className="font-semibold text-stone-800 mb-3">My Reservations</h3>
                                    {myReservations.length === 0 ? (
                                        <div className="bg-stone-100 rounded-xl p-6 text-center text-stone-500">
                                            No reservations yet
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {myReservations.map((res) => (
                                                <div key={res.id} className="bg-white rounded-xl p-4 border border-stone-200">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-semibold text-stone-800">
                                                                    {formatDate(res.date)}
                                                                </span>
                                                                <span className="text-stone-600">at {res.time}</span>
                                                            </div>
                                                            <div className="text-sm text-stone-500 mt-1">
                                                                Party of {res.party_size}
                                                                {res.table_number && ` • Table ${res.table_number}`}
                                                            </div>
                                                            {res.special_requests && (
                                                                <p className="text-xs text-stone-400 mt-1">{res.special_requests}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col items-end gap-2">
                                                            <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(res.status)}`}>
                                                                {res.status}
                                                            </span>
                                                            {res.status === 'pending' && (
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="sm"
                                                                    className="text-red-600 hover:text-red-700"
                                                                    onClick={() => handleCancelReservation(res.id)}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* AI Assistant Help */}
                                <div 
                                    onClick={() => navigate('/assistant')}
                                    className="bg-gradient-to-r from-rose-700 to-rose-800 rounded-2xl p-5 cursor-pointer hover:from-rose-800 hover:to-rose-900 transition-all"
                                    data-testid="ai-assistant-help"
                                >
                                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                                        <Phone className="w-5 h-5" />
                                        Need more assistance?
                                    </h3>
                                    <p className="text-rose-100 text-sm mb-3">
                                        Our AI assistant can help with reservations and questions.
                                    </p>
                                    <span className="text-white font-bold flex items-center gap-2">
                                        Chat with Assistant
                                        <ChevronRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Events Tab */}
                        {activeTab === 'events' && (
                            <div className="space-y-4">
                                <h2 className="text-lg font-semibold text-stone-800 mb-4">Upcoming Dining Events</h2>
                                {diningEvents.length === 0 ? (
                                    <div className="bg-stone-100 rounded-xl p-8 text-center">
                                        <Wine className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                        <p className="text-stone-500">No upcoming dining events</p>
                                    </div>
                                ) : (
                                    diningEvents.map((event) => (
                                        <div key={event.id} className="bg-white rounded-2xl p-5 border border-stone-200">
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                                                    <Wine className="w-6 h-6 text-rose-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="font-semibold text-stone-800">{event.title}</h3>
                                                    <p className="text-sm text-stone-500 mt-1">{event.description}</p>
                                                    <div className="flex items-center gap-4 mt-3 text-sm">
                                                        <span className="text-stone-600">
                                                            {formatDate(event.date)} at {event.time}
                                                        </span>
                                                        <span className="text-rose-600 font-medium">{event.price}</span>
                                                    </div>
                                                    <div className="text-xs text-stone-400 mt-1">
                                                        {event.capacity - event.current_bookings} spaces remaining
                                                    </div>
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-stone-400" />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Reservation Modal */}
            <Dialog open={reservationModal} onOpenChange={setReservationModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Book a Table</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={reservationForm.date}
                                onChange={(e) => setReservationForm({ ...reservationForm, date: e.target.value })}
                                min={new Date().toISOString().split('T')[0]}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Time</Label>
                            <select
                                value={reservationForm.time}
                                onChange={(e) => setReservationForm({ ...reservationForm, time: e.target.value })}
                                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg"
                            >
                                <option value="12:00">12:00 - Lunch</option>
                                <option value="12:30">12:30 - Lunch</option>
                                <option value="13:00">13:00 - Lunch</option>
                                <option value="18:00">18:00 - Dinner</option>
                                <option value="18:30">18:30 - Dinner</option>
                                <option value="19:00">19:00 - Dinner</option>
                                <option value="19:30">19:30 - Dinner</option>
                                <option value="20:00">20:00 - Dinner</option>
                            </select>
                        </div>
                        <div>
                            <Label>Party Size</Label>
                            <select
                                value={reservationForm.party_size}
                                onChange={(e) => setReservationForm({ ...reservationForm, party_size: parseInt(e.target.value) })}
                                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg"
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                                    <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label>Special Requests (optional)</Label>
                            <Textarea
                                value={reservationForm.special_requests}
                                onChange={(e) => setReservationForm({ ...reservationForm, special_requests: e.target.value })}
                                placeholder="e.g., window seat, birthday celebration, dietary requirements..."
                                rows={3}
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReservationModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateReservation} className="bg-rose-600 hover:bg-rose-700">
                            <Check className="w-4 h-4 mr-2" />
                            Request Reservation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default DiningPage;
